import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, map } from 'rxjs';

import { CoverageOption } from '../models/package-configurator/coverage-option.model';
import { CoverageOptionResponse, CoverageResponse, fromCoverageResponse, fromOptionResponse } from '../models/package-configurator/coverage-response.mapper';
import { PackageTemplate, PackageTemplateKind } from '../models/package-configurator/package-template.model';
import { PackageContent } from '../models/package-configurator/package.model';
import { PACKAGES_API_URL } from '../tokens/api.tokens';

/**
 * Access to reusable coverage / option templates via the `/v2/package-templates` API. A template is
 * always created FROM an already-persisted coverage or option (by id) and applied by reference (by
 * template id) — the backend does the snapshotting and reconstruction, so this service never builds or
 * parses a full template body itself.
 *
 * A template is immutable: there is no update and no delete endpoint. Names are unique per kind, so
 * creating one with a name that is already taken fails with 409.
 */
@Injectable({ providedIn: 'root' })
export class PackageTemplateService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(PACKAGES_API_URL);

  getTemplates(kind: PackageTemplateKind): Observable<PackageTemplate[]> {
    return this.http.get<PackageTemplate[]>(`${this.apiUrl}/v2/package-templates`, { params: new HttpParams().set('kind', kind) });
  }

  /** Saves an already-persisted coverage as a reusable content template. */
  createCoverageTemplate(sourceCoverageId: string, name: string): Observable<PackageTemplate> {
    return this.http.post<PackageTemplate>(`${this.apiUrl}/v2/package-templates/coverages`, { sourceCoverageId, name });
  }

  /** Saves an already-persisted option (of an already-persisted coverage) as a reusable option template. */
  createOptionTemplate(sourceCoverageId: string, sourceOptionId: string, name: string): Observable<PackageTemplate> {
    return this.http.post<PackageTemplate>(`${this.apiUrl}/v2/package-templates/options`, { sourceCoverageId, sourceOptionId, name });
  }

  /** Creates and attaches a new coverage on the package core from a content template, effective `activeFrom`. */
  createCoverageFromTemplate(packageCoreId: string, templateId: string, activeFrom: string): Observable<PackageContent> {
    return this.http
      .post<CoverageResponse>(`${this.apiUrl}/v2/packages/${packageCoreId}/coverages/from-template`, { templateId, activeFrom })
      .pipe(map(coverage => fromCoverageResponse(coverage, activeFrom)));
  }

  /** Adds an option template onto an already-persisted coverage. */
  addOptionFromTemplate(coverageId: string, templateId: string): Observable<CoverageOption> {
    return this.http.post<CoverageOptionResponse>(`${this.apiUrl}/v2/coverages/${coverageId}/options/from-template`, { templateId }).pipe(map(fromOptionResponse));
  }

  /**
   * Fetches an option template's full content. Used only when the target coverage is NOT persisted yet
   * (`addOptionFromTemplate` needs a real coverage id) — the option is then added locally as a draft and
   * persists along with the rest of the coverage on its next save.
   */
  getOptionTemplate(templateId: string): Observable<CoverageOption> {
    return this.http.get<CoverageOptionResponse>(`${this.apiUrl}/v2/package-templates/options/${templateId}`).pipe(map(fromOptionResponse));
  }
}
